import React, { useMemo, useCallback } from 'react';
import { FilterColumnProps } from '../types';
import { FixedMultiSelect, MultiSelectOption } from '../../Selectors/FixedMultiSelect';
import { FilterMultiSelect } from '../../Selectors/SimplifiedMultiSelect';
import { useColumnOptions } from '../../../lib/redux/filters/options/options-hook';
import { useUserData } from '../../../lib/redux/users/user-data-hook';
import { CustomMetricColumns } from '../../../interfaces/table';
import { CustomMetricValueType } from '../../../interfaces/db';

export function CategoricalFilter(props: FilterColumnProps) {
  const { value, onChange, useFixed = false } = props;
  const { getColumnOptions, hasOptions } = useColumnOptions();
  const { customDefinitions } = useUserData();
  
  // Helper function to check if this is a custom metric column
  const isCustomMetricColumn = (columnName: string): boolean => {
    return Object.values(CustomMetricColumns).includes(columnName as CustomMetricColumns) && 
           columnName !== CustomMetricColumns.SESSION_ID && 
           !columnName.startsWith('text_');
  };

  // Helper function to get custom metric definition
  const getCustomMetricDefinition = useCallback((columnName: string) => {
    if (!isCustomMetricColumn(columnName)) return null;
    
    const match = columnName.match(/^metric_(\d+)$/);
    if (!match) return null;
    
    const metricIdx = parseInt(match[1], 10);
    return customDefinitions.find(def => def.metric_idx === metricIdx);
  }, [customDefinitions]);

  // Check if this is a custom metric categorical column
  const isCustomMetricCategorical = useMemo(() => {
    if (!isCustomMetricColumn(value.column_name)) return false;
    const definition = getCustomMetricDefinition(value.column_name);
    return definition?.value_type === CustomMetricValueType.ENUM;
  }, [value.column_name, getCustomMetricDefinition]);
  
  // Get available options for this column
  const availableOptions = useMemo(() => {
    return getColumnOptions(value.column_name);
  }, [getColumnOptions, value.column_name]);
  

  // Convert current value_safe array to MultiSelectOption format
  const selectedOptions: MultiSelectOption[] = useMemo(() => {
    if (!Array.isArray(value.value_safe)) return [];
    
    return value.value_safe.map((val: any) => {
      // Convert both the stored value and option values to strings for comparison
      // since custom metrics store integers but options are strings
      const valAsString = val.toString();
      
      // Try to find the option in available options first for proper label
      const availableOption = availableOptions.find(option => option.value.toString() === valAsString);
      if (availableOption) {
        return availableOption;
      }
      
      // Fallback to creating option with value as label
      return {
        value: val,
        label: valAsString,
        column: { column_name: value.column_name, table: value.table }
      };
    });
  }, [value.value_safe, value.column_name, value.table, availableOptions]);

    // Don't render if no options are available
  if (!hasOptions(value.column_name)) {
    return null;
  }
  

  const handleChange = (newSelectedOptions: MultiSelectOption[]) => {
    let newValues: (string | number)[] = newSelectedOptions.map(option => option.value);
    
    // Convert string values to integers for custom metric categorical columns
    if (isCustomMetricCategorical) {
      newValues = newValues.map(val => {
        if (typeof val === 'string') {
          const parsed = parseInt(val, 10);
          return isNaN(parsed) ? val : parsed;
        }
        return val;
      });
    }
    
    onChange({
      ...value,
      value_safe: newValues
    });
  };

  const MultiSelectComponent = useFixed ? FixedMultiSelect : FilterMultiSelect;
  
  return (
    <div className="w-full">
      <MultiSelectComponent
        options={availableOptions}
        selected={selectedOptions}
        onChange={handleChange}
        placeholder="Select values..."
      />
    </div>
  );
} 